import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Modal } from './Modal';
import { FormInput, FormField, FormRow } from './FormFields';

interface CreateTripModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string, startDate: string, dayCount: number) => Promise<void>;
}

export function CreateTripModal({ open, onClose, onCreate }: CreateTripModalProps) {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [dayCount, setDayCount] = useState('5');
  const [isCreating, setIsCreating] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setName('');
      // Default to tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setStartDate(tomorrow.toISOString().split('T')[0]);
      setDayCount('5');
      setIsCreating(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim() || !startDate || isCreating) return;

    setIsCreating(true);
    try {
      await onCreate(name.trim(), startDate, parseInt(dayCount) || 5);
      onClose();
    } catch {
      setIsCreating(false);
    }
  };

  const canSubmit = name.trim() && startDate && !isCreating;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Trip"
      icon="add_location_alt"
      width="440px"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary" disabled={isCreating}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'px-5 py-2 text-[13px] font-semibold font-sans rounded-lg border-none cursor-pointer transition-all flex items-center gap-2',
              canSubmit
                ? 'bg-text-primary text-white hover:opacity-90'
                : 'bg-border text-text-muted cursor-default'
            )}
          >
            {isCreating ? (
              <>
                <span className="loading-spinner size-4 border-2 border-white/30 border-t-white" />
                Creating...
              </>
            ) : (
              'Create Trip'
            )}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField label="Trip Name">
          <FormInput
            value={name}
            onChange={setName}
            placeholder="e.g., Summer in Europe"
          />
        </FormField>

        <FormRow>
          <FormField label="Start Date" flex={1}>
            <FormInput
              value={startDate}
              onChange={setStartDate}
              type="date"
            />
          </FormField>
          <FormField label="Number of Days" flex={1}>
            <FormInput
              value={dayCount}
              onChange={setDayCount}
              type="number"
              min="1"
              placeholder="5"
            />
          </FormField>
        </FormRow>

        {/* Preview */}
        {startDate && dayCount && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-2">
            <div className="flex items-center gap-2 text-sm text-text-secondary mb-2">
              <span className="material-symbols-outlined text-base">
                preview
              </span>
              <span className="font-medium">Trip Preview</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs text-text-muted">
                <span className="material-symbols-outlined text-sm">calendar_today</span>
                {new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </div>
              <span className="text-text-muted">→</span>
              <div className="flex items-center gap-1.5 text-xs text-text-muted">
                <span className="material-symbols-outlined text-sm">event</span>
                {(() => {
                  const end = new Date(startDate + 'T00:00:00');
                  end.setDate(end.getDate() + (parseInt(dayCount) || 1) - 1);
                  return end.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  });
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
